import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  round1Display,
  round2Display,
  ratioToPercentPlus,
} from '../../lib/format';

const HEADER_GROUPS = [
  { title: '', columns: 2, color: 'rgba(0, 0, 0, 0)' },
  { title: 'Four Factors', columns: 4, color: '#ffe0b2' },
  { title: 'Assists', columns: 2, color: '#E1BEE7' },
  { title: 'Shooting', columns: 7, color: '#bbdefb' },
];
const HEADERS = [
  '',
  'POSS',
  'eFG%',
  'ORR',
  'FTR',
  'TOV%',
  'AST%',
  'ATO',
  'FG%',
  'FG3%',
  'FG2%',
  'FT%',
  'FG3 PAR',
  'PPS',
  'TS',
];

const formattedNet = (
  stats,
  key,
  isDefense = false,
  flip = false,
  rounder = ratioToPercentPlus,
  digits = 1,
  highlight = true,
) => {
  if (isDefense) key = 'opp_' + key;
  const on_value = get(stats, `on.${key}`);
  const off_value = get(stats, `off.${key}`);
  const value = on_value - off_value;
  let compValue = isDefense ? value * -1 : value;
  const sign = value >= 0 ? '+' : '';
  if (flip) compValue *= -1;
  const className = highlight
    ? compValue >= 0
      ? 'net--positive'
      : 'net--negative'
    : undefined;
  return (
    <span className={className}>{`${sign}${rounder(value, digits)}`}</span>
  );
};

const PlayerOnOffRow = ({ season, team, stats, label }) => {
  const { four_factors, assists, shooting_efficiency, possessions } = stats;
  return (
    <tr key={`${season.id}-${team.id}`}>
      <td className="nan label">{label}</td>
      <td>{Math.round(possessions)}</td>
      <td>{ratioToPercentPlus(four_factors.efg, 1)}</td>
      <td>{ratioToPercentPlus(four_factors.orr, 1)}</td>
      <td>{ratioToPercentPlus(four_factors.ftr, 1)}</td>
      <td>{ratioToPercentPlus(four_factors.tov, 1)}</td>
      <td>{ratioToPercentPlus(assists.assist_percentage, 1)}</td>
      <td>{round2Display(assists.ato, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.fg_perc, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.fg3_perc, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.fg2_perc, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.ft_perc, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.fg3_par, 1)}</td>
      <td>{round2Display(shooting_efficiency.pps_noft, 1)}</td>
      <td>{ratioToPercentPlus(shooting_efficiency.true_shooting, 1)}</td>
    </tr>
  );
};

const PlayerNetRow = ({ season, team, stats, isDefense = false }) => {
  return (
    <tr className="net" key={`${season.id}-${team.id}`}>
      <td className="nan label">Net</td>
      <td>
        {formattedNet(
          stats,
          'calculated.possessions',
          isDefense,
          false,
          Math.round,
          0,
          false,
        )}
      </td>
      <td>{formattedNet(stats, 'calculated.four_factors.efg', isDefense)}</td>
      <td>{formattedNet(stats, 'calculated.four_factors.orr', isDefense)}</td>
      <td>{formattedNet(stats, 'calculated.four_factors.ftr', isDefense)}</td>
      <td>
        {formattedNet(stats, 'calculated.four_factors.tov', isDefense, true)}
      </td>
      <td>
        {formattedNet(stats, 'calculated.assists.assist_percentage', isDefense)}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.assists.ato',
          isDefense,
          false,
          round2Display,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.fg_perc',
          isDefense,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.fg3_perc',
          isDefense,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.fg2_perc',
          isDefense,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.ft_perc',
          isDefense,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.fg3_par',
          isDefense,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.pps_noft',
          isDefense,
          false,
          round2Display,
        )}
      </td>
      <td>
        {formattedNet(
          stats,
          'calculated.shooting_efficiency.true_shooting',
          isDefense,
        )}
      </td>
    </tr>
  );
};

const PlayerOnOff = ({ player, isDefense }) => {
  const { stats } = player;
  const seasons =
    stats &&
    stats
      .filter(season => season.season.name.toLowerCase() !== 'career')
      .sort((a, b) => a.season.start_year - b.season.start_year);

  return (
    <div className="overflow-allowed">
      <table className="s-player-view__onoff">
        <thead>
          <tr>
            {HEADER_GROUPS.map(({ title, columns, color }) => (
              <th
                key={title}
                style={{ backgroundColor: color }}
                colSpan={columns}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {seasons.map(({ season, team, stats, played, games }) => (
            <React.Fragment key={`${season.id}-${team.id}`}>
              <tr className="group-header">
                <td colSpan={HEADERS.length}>
                  {season.name} &middot; {team.name}{' '}
                  <span className="caveat">
                    Active {played} / {games} Games (
                    {round1Display(stats.on.boxscore.minutes / played)}{' '}
                    mins/game)
                  </span>
                </td>
              </tr>
              <tr className="group-header">
                {HEADERS.map(header => (
                  <td key={header}>{header}</td>
                ))}
              </tr>
              <PlayerOnOffRow
                label={'On'}
                team={team}
                season={season}
                stats={
                  isDefense ? stats.on.opp_calculated : stats.on.calculated
                }
              />
              <PlayerOnOffRow
                label={'Off'}
                team={team}
                season={season}
                stats={
                  isDefense ? stats.off.opp_calculated : stats.off.calculated
                }
              />
              <PlayerNetRow
                team={team}
                season={season}
                stats={stats}
                isDefense={isDefense}
              />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PlayerOnOff.propTypes = {
  player: PropTypes.object.isRequired,
  isDefense: PropTypes.bool,
};

export default PlayerOnOff;
